import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Criteria, Quotas } from '../../interfaces/survey.interface';
import {
  BlackBoxPayloadInterface,
  BLACK_BOX_SUPPORTED_QUALIFICATIONS,
} from '../../constants/black-box-constants';
import { EnvironmentConfig } from '../../interfaces/environment.interface';
import {
  BlackBoxInputPayload,
  Permutations,
  RecommendedPrice,
  Survey,
} from '../../interfaces/black-box.interface';
import { Survey as SurveyData } from '../../../shared/interfaces/survey.interface';

@Injectable({
  providedIn: 'root',
})
export class BlackBoxService {
  private readonly _priceRecoUrl =
    this._env.environment.pureSpectrum.priceRecommendUrl;
  private readonly _baseUrl = this._env.environment.pureSpectrum.url;

  constructor(
    private readonly _http: HttpClient,
    @Inject('ENV') private _env: EnvironmentConfig
  ) {}

  generateBlackBoxPayload(
    quota: Quotas | any,
    surveyData: Survey
  ): BlackBoxInputPayload {
    const completesRemaining =
      quota.currently_open ||
      surveyData.completes_required - surveyData.fielded;

    let blackBoxInputPayload = {
      survey_id: surveyData.ps_survey_id,
      country: surveyData.locale.country_name,
      completes_remaining: completesRemaining,
      IR: surveyData.incidence.recent,
      LOI: surveyData.loi.recent,
      statistics: true,
      field_time: surveyData.field_time,
    };

    const qualificationPayload = this._generateQualificationPayload(quota);
    blackBoxInputPayload = { ...blackBoxInputPayload, ...qualificationPayload };
    return blackBoxInputPayload;
  }

  private _generateQualificationPayload(quota: Quotas) {
    let payload: BlackBoxPayloadInterface = {};

    for (let qualificationCode in BLACK_BOX_SUPPORTED_QUALIFICATIONS) {
      const value = BLACK_BOX_SUPPORTED_QUALIFICATIONS[qualificationCode];
      payload[value.key] = value.default;
    }

    quota.criteria.forEach((item) => {
      payload = { ...payload, ...this._getConditionalBasedPayload(item) };
    });
    return payload;
  }

  private _getConditionalBasedPayload(item: Criteria) {
    let payload: BlackBoxPayloadInterface = {};
    if (BLACK_BOX_SUPPORTED_QUALIFICATIONS[item.qualification_code]) {
      payload[BLACK_BOX_SUPPORTED_QUALIFICATIONS[item.qualification_code].key] =
        BLACK_BOX_SUPPORTED_QUALIFICATIONS[item.qualification_code].type ===
        'range'
          ? `${item.range_sets[0].from}-${item.range_sets[0].to}`
          : item.condition_names[0];
    }
    return payload;
  }

  mapIntoBlackboxSurveyData(survey: SurveyData): Survey {
    return {
      completes_required: survey.goal,
      fielded: survey.fielded,
      ps_survey_id: survey.id,
      incidence: {
        recent: survey.incidence.recent,
      },
      loi: {
        recent: survey.loi.recent,
      },
      field_time: survey.field_time || 0,
      locale: {
        country_name: survey.countryName,
        language_code: survey.countryCode,
      },
      is_pure_price_rate_card_enable:
        survey.is_pure_price_rate_card_enable || false,
    };
  }

  getRecommendedCpi(payload: BlackBoxInputPayload) {
    return this._http.post<RecommendedPrice>(
      `${this._priceRecoUrl}/v1/pricing-reco`,
      payload
    );
  }

  calculatePermutations(payload: BlackBoxInputPayload) {
    return this._http.post<Permutations>(
      `${this._priceRecoUrl}/v1/calculate-permutations`,
      payload
    );
  }

  pricingRecoFlyOutOpened(payload: { survey_id: number }) {
    return this._http.patch<{ pricingRecoFlyOutOpened: boolean }>(
      `${this._baseUrl}/v1/pricing-reco/fly-out-opened`,
      payload
    );
  }
}
